import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Heart, Calendar, Navigation, ExternalLink } from "lucide-react";
import { cn, whoWants } from "@/lib/utils";
import { buildAppleMapsLink } from "@/lib/maps";
import { compositeScore20, scoreToneClass, scoreToneBadgeClasses, tasteAverageScore, tasteFineScore, tasteGrade } from "@/lib/rating";

interface Restaurant {
  id: string;
  name: string;
  cuisine: string;
  location: string;
  rating?: number;
  ratingTaste?: number;
  ratingTasteBalance?: number;
  ratingTasteIngredients?: number;
  ratingTasteTechnique?: number;
  ratingTasteFine?: number;
  ratingTasteGrade?: "S" | "A" | "B" | "C" | "D";
  ratingAmbience?: number;
  ratingService?: number;
  ratingCost?: number;
  visitDate?: string;
  visitHistory?: string[];
  isWishlist: boolean;
  image?: string;
  notes?: string;
  priceRange?: string;
  sourceUrl?: string;
  tiktokUrl?: string;
  lat?: number;
  lon?: number;
  prefecture?: string;
  municipality?: string;
  wishlistBy?: string[];
}

interface RestaurantCardProps {
  restaurant: Restaurant;
  onClick?: () => void;
  className?: string;
}

export function RestaurantCard({ restaurant, onClick, className }: RestaurantCardProps) {
  const tasteAverage = tasteAverageScore(restaurant as any);
  const tasteFine = tasteFineScore(restaurant as any);
  const tasteGradeValue = tasteGrade(restaurant as any);

  return (
    <Card
      className={cn(
        "group cursor-pointer overflow-hidden bg-card shadow-card",
        "hover:shadow-warm transition-all duration-300 hover:scale-[1.02]",
        "border-0 bg-gradient-to-br from-card to-card/80",
        className
      )}
      onClick={onClick}
    >
      {/* Image Header */}
      <div className="relative h-40 sm:h-48 overflow-hidden bg-gradient-warm">
        {restaurant.image ? (
          <img
            src={restaurant.image}
            alt={restaurant.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-warm">
            <div className="text-center text-muted-foreground">
              <MapPin className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p className="text-sm">画像なし</p>
            </div>
          </div>
        )}
        
        {/* Wishlist Heart */}
        {restaurant.isWishlist && (
          <div className="absolute top-3 right-3">
            <div className="rounded-full bg-background/90 p-2 shadow-soft">
              <Heart className="h-4 w-4 fill-primary text-primary" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-base sm:text-lg text-card-foreground leading-tight">
              {restaurant.name}
            </h3>
            {(() => {
              const s = compositeScore20(restaurant as any);
              return (
                <div className="flex flex-col items-end gap-1">
                  <div
                    className={cn(
                      "inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md border shadow-sm",
                      scoreToneBadgeClasses(s),
                    )}
                    aria-label="総合評価"
                  >
                    <span className="text-muted-foreground/80">総合</span>
                    <span className={cn("font-semibold", scoreToneClass(s))}>{s}点</span>
                  </div>
                  {tasteGradeValue && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md border border-border/60 text-muted-foreground">
                      <span>味 {tasteGradeValue}</span>
                      {tasteFine > 0 && <span className="text-muted-foreground/60">9段階 {tasteFine.toFixed(1)}</span>}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{restaurant.location}</span>
          </div>
        </div>

        {/* Cuisine + Price */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            {restaurant.cuisine}
          </Badge>
          {restaurant.priceRange && (
            <Badge variant="outline" className="text-xs">
              {restaurant.priceRange}
            </Badge>
          )}
        </div>

        {/* Visit Date */}
        {restaurant.visitDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>訪問日 {restaurant.visitDate}</span>
          </div>
        )}
        {Array.isArray(restaurant.visitHistory) && restaurant.visitHistory.length > 0 && (
          <div className="text-xs text-muted-foreground">訪問 {restaurant.visitHistory.length}回</div>
        )}
        {restaurant.isWishlist && Array.isArray(restaurant.wishlistBy) && restaurant.wishlistBy.length > 0 && (
          <div className="text-xs text-muted-foreground">食べたい: {whoWants(restaurant.wishlistBy).join('・')}</div>
        )}

        {/* Notes Preview */}
        {restaurant.notes && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {restaurant.notes}
          </p>
        )}

        {/* Rating Breakdown */}
        {(restaurant.ratingTaste || restaurant.ratingAmbience || restaurant.ratingService || restaurant.ratingCost) && (
          <div className="text-[11px] text-muted-foreground mt-1">
            <span>味 {restaurant.ratingTaste ?? '-'}・</span>
            <span>雰 {restaurant.ratingAmbience ?? '-'}・</span>
            <span>接 {restaurant.ratingService ?? '-'}・</span>
            <span>価 {restaurant.ratingCost ?? '-'}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              const url = buildAppleMapsLink(restaurant.name, restaurant.location, restaurant.lat as any, restaurant.lon as any, restaurant.prefecture, restaurant.municipality);
              window.open(url, '_blank');
            }}
          >
            <Navigation className="h-4 w-4 mr-1" /> Appleマップで開く
          </Button>
          {restaurant.tiktokUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(restaurant.tiktokUrl, '_blank', 'noopener');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-1" /> TikTok
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
